import { Category } from '@/class/Category';
import { ItemMaster } from '@/class/ItemMaster';
import { HTTPResponse } from '@/class/HTTPResponse';
import { Option } from '@/class/Option';
import { fetchData, postData, postFormData, putData } from '../../../lib/api';
import SystemAdminLayout from '../../layout/SystemAdminLayout';
import ItemCreateModal from './createModal';
import ItemEditModal, { ItemEditForm } from './editModal';
import { emptyItemForm, itemLevelLabel, parseItemForm } from './itemForm';
import {
  adminCard,
  adminSectionTitle,
  adminTableCell,
  adminTableHead,
  adminButtonPrimary,
  adminPageToolbar,
  adminTableWrap,
  adminTable,
  adminTableCellName,
  adminMutedText,
} from '../../layout/adminStyles';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

const ITEM_CSV_TEMPLATE = `category,name,furigana,level
野菜,新規商品名,しんきしょうひんめい,1`;

type ItemImportResult = {
  created_count: number;
  error_count: number;
  errors: Array<{ row: number; message: string }>;
};

type GeneratePricesResult = {
  store_count: number;
  item_count: number;
  updated_count: number;
  skipped_count: number;
  unmatched_names: string[];
};

const SystemAdminItemIndexPage = () => {
  const [items, setItems] = useState<ItemMaster[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isShowEditModal, setIsShowEditModal] = useState<boolean>(false);
  const [isShowCreateModal, setIsShowCreateModal] = useState<boolean>(false);
  const [editingItemId, setEditingItemId] = useState<number | null>(null);
  const [editForm, setEditForm] = useState<ItemEditForm>(emptyItemForm());
  const [createForm, setCreateForm] = useState<ItemEditForm>(emptyItemForm());
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [isImporting, setIsImporting] = useState<boolean>(false);
  const [importResult, setImportResult] = useState<ItemImportResult | null>(null);
  const [isGeneratingPrices, setIsGeneratingPrices] = useState<boolean>(false);
  const [generatePricesResult, setGeneratePricesResult] = useState<GeneratePricesResult | null>(null);
  const csvInputRef = useRef<HTMLInputElement>(null);
  const priceCsvInputRef = useRef<HTMLInputElement>(null);

  const categoryOptions: Option[] = categories.map((category) => ({
    id: String(category.id),
    name: category.name,
  }));

  const allSelected = useMemo(
    () => items.length > 0 && selectedIds.length === items.length,
    [items, selectedIds]
  );

  const loadCategories = useCallback(async () => {
    try {
      const res: HTTPResponse = await fetchData('category');
      setCategories(Array.isArray(res.data) ? res.data : []);
    } catch (err: unknown) {
      console.error(err);
      setCategories([]);
    }
  }, []);

  const loadItems = useCallback(async () => {
    setIsLoading(true);
    try {
      const res: HTTPResponse = await postData('item', {});
      setItems(Array.isArray(res.data) ? res.data : []);
      setSelectedIds([]);
    } catch (err: unknown) {
      console.error(err);
      setItems([]);
      setSelectedIds([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCategories();
    loadItems();
  }, [loadCategories, loadItems]);

  const toggleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedIds(items.map((item) => item.item_id));
      return;
    }
    setSelectedIds([]);
  };

  const toggleSelectOne = (itemId: number, checked: boolean) => {
    setSelectedIds((prev) => {
      if (checked) {
        return prev.includes(itemId) ? prev : [...prev, itemId];
      }
      return prev.filter((id) => id !== itemId);
    });
  };

  const openEditModal = (item: ItemMaster) => {
    setEditingItemId(item.item_id);
    setEditForm({
      item_name: item.item_name ?? '',
      furigana: item.furigana ?? '',
      category_id: item.category_id != null ? String(item.category_id) : '',
      level: item.level != null ? String(item.level) : '',
    });
    setIsShowEditModal(true);
  };

  const closeEditModal = () => {
    setIsShowEditModal(false);
    setEditingItemId(null);
    setEditForm(emptyItemForm());
  };

  const openCreateModal = () => {
    setCreateForm(emptyItemForm());
    setIsShowCreateModal(true);
  };

  const closeCreateModal = () => {
    setIsShowCreateModal(false);
    setCreateForm(emptyItemForm());
  };

  const handleSave = async () => {
    if (editingItemId == null) {
      return;
    }

    const payload = parseItemForm(editForm);
    if (!payload) {
      return;
    }

    setIsSaving(true);
    try {
      const res: HTTPResponse = await putData(`item/${editingItemId}`, payload);

      if (res.data) {
        const updated = res.data as ItemMaster;
        setItems((prev) =>
          prev.map((item) =>
            item.item_id === editingItemId ? { ...item, ...updated } : item
          )
        );
        closeEditModal();
      }
    } catch (err: unknown) {
      console.error(err);
      alert('商品の更新に失敗しました。');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCreate = async () => {
    const payload = parseItemForm(createForm);
    if (!payload) {
      return;
    }

    setIsSaving(true);
    try {
      const res: HTTPResponse = await postData('item/create', payload);

      if (res.data) {
        const created = res.data as ItemMaster;
        setItems((prev) => [...prev, created].sort((a, b) => a.item_id - b.item_id));
        closeCreateModal();
      }
    } catch (err: unknown) {
      console.error(err);
      alert('商品の登録に失敗しました。');
    } finally {
      setIsSaving(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0 || isDeleting) {
      return;
    }

    if (
      !window.confirm(
        `選択した${selectedIds.length}件の商品を削除しますか？\n店舗に登録されている価格・プラン選択も解除されます。`
      )
    ) {
      return;
    }

    setIsDeleting(true);
    try {
      const res: HTTPResponse = await postData('item/delete', { item_ids: selectedIds });
      const deletedCount =
        res.data && typeof res.data === 'object' && 'deleted_count' in res.data
          ? Number((res.data as { deleted_count: number }).deleted_count)
          : selectedIds.length;

      alert(`${deletedCount}件の商品を削除しました。`);
      await loadItems();
    } catch (err: unknown) {
      console.error(err);
      alert('商品の削除に失敗しました。');
    } finally {
      setIsDeleting(false);
    }
  };

  const downloadCsvTemplate = () => {
    const blob = new Blob([`\uFEFF${ITEM_CSV_TEMPLATE}`], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'items_template.csv';
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleCsvFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';

    if (!file) {
      return;
    }

    setIsImporting(true);
    setImportResult(null);

    try {
      const formData = new FormData();
      formData.append('file', file);
      const res: HTTPResponse = await postFormData('item/import', formData);

      if (res.data) {
        setImportResult(res.data as ItemImportResult);
        await loadItems();
      }
    } catch (err: unknown) {
      console.error(err);
      alert('CSVのアップロードに失敗しました。');
    } finally {
      setIsImporting(false);
    }
  };

  const handlePriceCsvFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';

    if (!file) {
      return;
    }

    if (
      !window.confirm(
        'CSVの基準価格をもとに、LIGHT商品の店舗価格を一括生成します。\n価格が未入力（0円）の店舗商品だけが対象です。よろしいですか？'
      )
    ) {
      return;
    }

    setIsGeneratingPrices(true);
    setGeneratePricesResult(null);

    try {
      const formData = new FormData();
      formData.append('file', file);
      const res: HTTPResponse = await postFormData('item/generate-prices', formData);

      if (res.data) {
        setGeneratePricesResult(res.data as GeneratePricesResult);
      }
    } catch (err: unknown) {
      console.error(err);
      alert('店舗価格の一括生成に失敗しました。');
    } finally {
      setIsGeneratingPrices(false);
    }
  };

  return (
    <SystemAdminLayout>
      <div className={adminCard}>
        <div className={adminPageToolbar}>
          <h2 className={adminSectionTitle + ' mb-0 border-0 pb-0'}>商品マスタ</h2>
          <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row">
            <button
              type="button"
              className={`${adminButtonPrimary} w-full sm:w-auto bg-stone-600 hover:bg-stone-700`}
              onClick={downloadCsvTemplate}
            >
              CSVテンプレート
            </button>
            <button
              type="button"
              className={`${adminButtonPrimary} w-full sm:w-auto bg-stone-600 hover:bg-stone-700`}
              onClick={() => csvInputRef.current?.click()}
              disabled={isImporting}
            >
              {isImporting ? 'アップロード中...' : 'CSVアップロード'}
            </button>
            <button
              type="button"
              className={`${adminButtonPrimary} w-full sm:w-auto bg-amber-500 hover:bg-amber-600 shadow-amber-500/20`}
              onClick={() => priceCsvInputRef.current?.click()}
              disabled={isGeneratingPrices}
            >
              {isGeneratingPrices ? '生成中...' : '店舗価格を一括生成'}
            </button>
            <button type="button" className={`${adminButtonPrimary} w-full sm:w-auto`} onClick={openCreateModal}>
              新規登録
            </button>
          </div>
        </div>
        <input
          ref={csvInputRef}
          type="file"
          accept=".csv,text/csv"
          className="hidden"
          onChange={handleCsvFileChange}
        />
        <input
          ref={priceCsvInputRef}
          type="file"
          accept=".csv,text/csv"
          className="hidden"
          onChange={handlePriceCsvFileChange}
        />
        <p className={`${adminMutedText} text-sm mb-2`}>
          CSVの列: <code>category</code>, <code>name</code>（必須）、<code>furigana</code>, <code>level</code>（任意・1=LIGHT / 2=その他）。
          category はカテゴリマスタの名称（例: 野菜、精肉）を指定してください。新規登録のみです。
        </p>
        <p className={`${adminMutedText} text-sm mb-4`}>
          「店舗価格を一括生成」は同じCSVの<strong>5列目の金額</strong>を基準価格として使います。
          <code>level</code> が 1 の商品について、基準価格に±10%のばらつきをつけた税抜金額を店舗ごとに設定し、
          税込金額は8%（アルコールは10%）で計算します。価格が未入力（0円）の店舗商品のみが対象です。
        </p>
        {importResult && (
          <div className="mb-4 rounded-xl border border-stone-200 bg-stone-50 px-4 py-3 text-sm text-stone-700">
            <p>
              新規 {importResult.created_count} 件を登録しました。
              {importResult.error_count > 0 && (
                <span className="text-red-600"> エラー {importResult.error_count} 件</span>
              )}
            </p>
            {importResult.errors.length > 0 && (
              <ul className="mt-2 list-disc space-y-1 pl-5 text-red-600">
                {importResult.errors.map((error) => (
                  <li key={`${error.row}-${error.message}`}>
                    {error.row}行目: {error.message}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
        {generatePricesResult && (
          <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-stone-700">
            <p>
              {generatePricesResult.store_count} 店舗 / {generatePricesResult.item_count} 商品に価格を設定しました（
              {generatePricesResult.updated_count} 件更新）。
              {generatePricesResult.skipped_count > 0 && (
                <span> 既に価格が入っている {generatePricesResult.skipped_count} 件はスキップしました。</span>
              )}
            </p>
            {generatePricesResult.unmatched_names.length > 0 && (
              <p className="mt-2 text-red-600">
                商品マスタに見つからなかったCSVの商品: {generatePricesResult.unmatched_names.join('、')}
              </p>
            )}
          </div>
        )}
        <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm text-stone-600 mb-0">全 {items.length} 件</p>
          <button
            type="button"
            className={`${adminButtonPrimary} w-full sm:w-auto bg-red-500 hover:bg-red-600 shadow-red-500/20 disabled:cursor-not-allowed disabled:opacity-50`}
            onClick={handleBulkDelete}
            disabled={selectedIds.length === 0 || isDeleting || isLoading}
          >
            {isDeleting ? '削除中...' : `選択した商品を削除${selectedIds.length > 0 ? `（${selectedIds.length}）` : ''}`}
          </button>
        </div>


        <div className={adminTableWrap}>
        <table className={adminTable}>
          <thead>
            <tr className={adminTableHead}>
              <th className={`${adminTableCell} text-center w-12`}>
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={(e) => toggleSelectAll(e.target.checked)}
                  disabled={isLoading || items.length === 0}
                  aria-label="すべて選択"
                />
              </th>
              <th className={`${adminTableCell} text-left w-12`}>ID</th>
              <th className={`${adminTableCellName} text-left`}>商品名</th>
              <th className={`${adminTableCell} text-left`}>ふりがな</th>
              <th className={`${adminTableCell} text-left`}>カテゴリ</th>
              <th className={`${adminTableCell} text-center w-20`}>レベル</th>
              <th className={`${adminTableCell} text-center w-24`}>操作</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={7} className={`${adminTableCell} p-6 text-center text-stone-500`}>
                  読み込み中...
                </td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td colSpan={7} className={`${adminTableCell} p-6 text-center text-stone-500`}>
                  商品がありません。
                </td>
              </tr>
            ) : (
              items.map((item: ItemMaster) => (
                <tr key={item.item_id}>
                  <td className={`${adminTableCell} text-center`}>
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(item.item_id)}
                      onChange={(e) => toggleSelectOne(item.item_id, e.target.checked)}
                      aria-label={`${item.item_name}を選択`}
                    />
                  </td>
                  <td className={adminTableCell}>{item.item_id}</td>
                  <td className={adminTableCellName} title={item.item_name ?? ''}>{item.item_name}</td>
                  <td className={adminTableCell}>{item.furigana ?? '-'}</td>
                  <td className={adminTableCell}>{item.category_name ?? '-'}</td>
                  <td className={`${adminTableCell} text-center`}>{itemLevelLabel(item.level)}</td>
                  <td className={`${adminTableCell} text-center`}>
                    <button
                      type="button"
                      className={`${adminButtonPrimary} text-sm py-1 px-3`}
                      onClick={() => openEditModal(item)}
                    >
                      編集
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        </div>
      </div>

      <ItemEditModal
        isShowModal={isShowEditModal}
        itemId={editingItemId}
        editForm={editForm}
        categoryOptions={categoryOptions}
        isSaving={isSaving}
        onChange={setEditForm}
        onClose={closeEditModal}
        onSubmit={handleSave}
      />

      <ItemCreateModal
        isShowModal={isShowCreateModal}
        form={createForm}
        categoryOptions={categoryOptions}
        isSaving={isSaving}
        onChange={setCreateForm}
        onClose={closeCreateModal}
        onSubmit={handleCreate}
      />
    </SystemAdminLayout>
  );
};

export default SystemAdminItemIndexPage;
